// Video Block — YouTube / Vimeo / direct video URL or upload
import { escapeAttr, performMediaUpload, canUploadMedia } from './blockUtils';

class VideoBlock {
  static get toolbox() {
    return {
      title: 'Video',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, readOnly }) {
    this.data = {
      url: data.url || '',
      caption: data.caption || ''
    };
    this.readOnly = readOnly;
    this.wrapper = null;
    this.mediaUpload = (config && config.mediaUpload) || {};
    this._uploading = false;
  }

  render() {
    this.wrapper = document.createElement('div');
    this._render();
    return this.wrapper;
  }

  _spaceId() {
    return this.mediaUpload.spaceId || '';
  }

  _getEmbedIframeSrc(url) {
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return { type: 'iframe', src: `https://www.youtube.com/embed/${ytMatch[1]}` };
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
    return { type: 'video', src: url };
  }

  _render() {
    const eu = escapeAttr;
    const urlRaw = this.data.url || '';
    const urlVal = eu(urlRaw);
    const capVal = eu(this.data.caption);

    let mediaHtml = '';
    if (urlRaw) {
      const { type, src } = this._getEmbedIframeSrc(urlRaw);
      if (type === 'iframe') {
        const s = eu(src);
        mediaHtml = `
          <div class="relative pb-[56.25%] rounded-lg overflow-hidden bg-slate-900">
            <iframe src="${s}" class="absolute inset-0 w-full h-full" frameborder="0" allowfullscreen title="Video"></iframe>
          </div>`;
      } else {
        mediaHtml = `
          <video src="${urlVal}" controls class="w-full rounded-lg max-h-[70vh]">${eu('Your browser does not support the video tag.')}</video>`;
      }
    }

    this.wrapper.innerHTML = `
      <div class="video-block max-w-3xl mx-auto" data-testid="video-block">
        ${!this.readOnly ? `
          <div class="flex flex-wrap items-center gap-2 mb-2">
            <input type="text" class="url-input flex-1 min-w-[12rem] px-3 py-2 rounded border border-slate-200 dark:border-slate-600 text-sm bg-white dark:bg-slate-900" placeholder="YouTube, Vimeo, or video file URL…" value="${urlVal}" />
            <input type="file" class="video-file-input hidden" accept="video/mp4,video/webm,video/ogg,.mp4,.webm,.ogg,.mov,.m4v" />
            <button type="button" class="video-upload-btn px-3 py-2 text-sm rounded border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" ${this._uploading ? 'disabled' : ''}>
              ${this._uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          <p class="video-upload-err text-xs text-red-600 mb-2 hidden"></p>
        ` : ''}
        ${urlRaw ? `
          ${mediaHtml}
          ${this.data.caption || !this.readOnly ? `
            <div class="mt-2 text-center">
              ${!this.readOnly ? `
                <input type="text" class="caption-input w-full px-2 py-1 text-sm text-slate-500 bg-transparent border-0 focus:outline-none text-center" placeholder="Add a caption..." value="${capVal}" />
              ` : `<p class="text-sm text-slate-500">${eu(this.data.caption)}</p>`}
            </div>
          ` : ''}
        ` : `
          <div class="p-8 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center text-slate-400 text-sm">
            Paste a video link (YouTube, Vimeo, or direct .mp4/.webm) or upload a file
          </div>
        `}
      </div>
    `;

    if (!this.readOnly) {
      const errEl = this.wrapper.querySelector('.video-upload-err');
      const showErr = (msg) => {
        if (!errEl) return;
        errEl.textContent = msg || '';
        errEl.classList.toggle('hidden', !msg);
      };

      this.wrapper.querySelector('.url-input')?.addEventListener('input', (e) => {
        this.data.url = e.target.value;
        showErr('');
        this._render();
      });
      this.wrapper.querySelector('.caption-input')?.addEventListener('input', (e) => {
        this.data.caption = e.target.value;
      });

      const fileInput = this.wrapper.querySelector('.video-file-input');
      const uploadBtn = this.wrapper.querySelector('.video-upload-btn');

      uploadBtn?.addEventListener('click', () => {
        showErr('');
        if (!canUploadMedia(this.mediaUpload)) {
          showErr('Uploads are not available in this context.');
          return;
        }
        fileInput?.click();
      });

      fileInput?.addEventListener('change', async () => {
        const file = fileInput.files && fileInput.files[0];
        fileInput.value = '';
        if (!file) return;
        this._uploading = true;
        this._render();
        try {
          const res = await performMediaUpload(this.mediaUpload, file, 'video');
          this.data.url = res.url || '';
          showErr('');
        } catch (e) {
          showErr(e.message || 'Upload failed');
        } finally {
          this._uploading = false;
          this._render();
        }
      });
    }
  }

  save() {
    return this.data;
  }
}

export default VideoBlock;
